import { X, Check, XCircle, Sparkles, AlertCircle } from 'lucide-react';

export default function RewriteModal({ beat, rewriteData, isLoading, error, onAccept, onReject, onClose }) {
  if (!beat) return null;

  const beatName = beat.beat?.name || beat.sequence?.name || 'This Beat';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{beatName} - AI Rewrite</h2>
            <p className="text-sm text-slate-600">Pages {beat.startPage}-{beat.endPage}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex flex-col items-center justify-center h-full py-20">
              <Sparkles className="w-16 h-16 text-purple-600 animate-pulse mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">AI is rewriting this beat...</h3>
              <p className="text-slate-600">This usually takes 10-20 seconds</p>
            </div>
          )}

          {error && (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900 mb-2">Rewrite Failed</h3>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          {rewriteData && !isLoading && !error && (
            <div className="grid grid-cols-2 gap-0 h-full">
              {/* Original Version */}
              <div className="border-r border-slate-200 p-6">
                <div className="sticky top-0 bg-white pb-3 mb-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900 flex items-center space-x-2">
                    <span>Original Version</span>
                    <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded">
                      {beat.health}
                    </span>
                  </h3>
                </div>
                <pre className="whitespace-pre-wrap font-mono text-sm text-slate-700 leading-relaxed">
                  {rewriteData.original}
                </pre>
              </div>

              {/* Rewritten Version */}
              <div className="p-6 bg-purple-50/30">
                <div className="sticky top-0 bg-purple-50/30 pb-3 mb-4 border-b border-purple-200">
                  <h3 className="font-semibold text-purple-900 flex items-center space-x-2">
                    <Sparkles className="w-4 h-4" />
                    <span>AI Improved Version</span>
                  </h3>
                </div>
                <pre className="whitespace-pre-wrap font-mono text-sm text-slate-700 leading-relaxed">
                  {rewriteData.rewritten}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {rewriteData && !isLoading && !error && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-sm text-slate-600">
              <p>Review the changes and choose to accept or keep the original</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onReject}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors flex items-center space-x-2"
              >
                <XCircle className="w-4 h-4" />
                <span>Keep Original</span>
              </button>
              <button
                onClick={onAccept}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
              >
                <Check className="w-4 h-4" />
                <span>Accept Rewrite</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
